import { useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import api from "../services/api";

const CATEGORIES = [
  { value: "general",     label: "💬 General Inquiry",     desc: "Questions about TumaTrack" },
  { value: "support",     label: "🛠️ Technical Support",    desc: "App issues or bugs" },
  { value: "billing",     label: "💳 Billing & Payments",   desc: "Subscription or M-Pesa issues" },
  { value: "partnership", label: "🤝 Partnership",          desc: "Business collaborations" },
  { value: "other",       label: "📋 Other",                desc: "Anything else" },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    subject: "", category: "general", message: "",
  });
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState("");

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "", category: "general", message: "" });
    } catch (err) {
      setError(
        err.response?.data?.message ||
        Object.values(err.response?.data?.errors || {}).flat().join(" ") ||
        "Failed to send message. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* ── HERO ── */}
      <section className="bg-slate-900 pt-36 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle at 30% 60%, rgba(249,115,22,0.6) 0%, transparent 60%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-5 text-center">
          <p className="text-orange-400 font-semibold text-sm uppercase tracking-widest mb-4">
            Contact Us
          </p>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6"
            style={{ fontFamily: "'Sora', sans-serif" }}>
            We'd love to{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
              hear from you
            </span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Whether you have a question, need support, or want to explore a partnership —
            our team responds within 24 hours.
          </p>
        </div>
      </section>

      {/* ── CONTACT CARDS ── */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-5 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              icon: "💬",
              label: "WhatsApp",
              value: "+254 769 177 634",
              sub: "Mon–Fri, 8am–6pm",
              href: "https://wa.me/254769177634",
              color: "bg-green-50 border-green-100",
              btn: "bg-green-500 hover:bg-green-600",
            },
            {
              icon: "📧",
              label: "Email Us",
              value: "tumatrack.app@gmail.com",
              sub: "We reply within 24 hours",
              href: "mailto:tumatrack.app@gmail.com",
              color: "bg-blue-50 border-blue-100",
              btn: "bg-blue-500 hover:bg-blue-600",
            },
            {
              icon: "🛠️",
              label: "Technical Support",
              value: "tumatrack.app@gmail.com",
              sub: "For app issues & bugs",
              href: "mailto:tumatrack.app@gmail.com",
              color: "bg-orange-50 border-orange-100",
              btn: "bg-orange-500 hover:bg-orange-600",
            },
          ].map((c) => (
            <a key={c.label} href={c.href} target="_blank" rel="noreferrer"
              className={`flex flex-col items-center text-center p-6 rounded-2xl border ${c.color} hover:shadow-md transition-all group`}>
              <span className="text-4xl mb-3">{c.icon}</span>
              <p className="font-bold text-slate-900 mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
                {c.label}
              </p>
              <p className="text-slate-700 text-sm font-medium mb-1">{c.value}</p>
              <p className="text-slate-400 text-xs mb-4">{c.sub}</p>
              <span className={`text-white text-xs font-semibold px-4 py-1.5 rounded-full ${c.btn} transition-colors`}>
                Contact →
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* ── MAIN FORM ── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10">

            {/* Left — info */}
            <div className="md:col-span-2 space-y-6">
              <div>
                <p className="text-orange-500 font-semibold text-sm uppercase tracking-widest mb-3">
                  Get In Touch
                </p>
                <h2 className="text-3xl font-bold text-slate-900 mb-4"
                  style={{ fontFamily: "'Sora', sans-serif" }}>
                  Send us a message
                </h2>
                <p className="text-slate-500 leading-relaxed text-sm">
                  Fill in the form and our team will get back to you promptly.
                  For faster response, WhatsApp us directly.
                </p>
              </div>

              {/* Categories info */}
              <div className="space-y-3">
                <p className="text-slate-700 font-semibold text-sm">What can we help with?</p>
                {CATEGORIES.map((c) => (
                  <div key={c.value} className="flex items-center gap-3">
                    <span className="text-base">{c.label.split(" ")[0]}</span>
                    <div>
                      <p className="text-slate-700 text-xs font-semibold">{c.label.split(" ").slice(1).join(" ")}</p>
                      <p className="text-slate-400 text-xs">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Office info */}
              <div className="bg-white border border-slate-100 rounded-2xl p-5">
                <p className="font-bold text-slate-900 text-sm mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>
                  📍 Our Office
                </p>
                <p className="text-slate-500 text-sm">Nairobi, Kenya 🇰🇪</p>
                <p className="text-slate-500 text-sm mt-1">Mon – Fri: 8:00 AM – 6:00 PM</p>
                <p className="text-slate-500 text-sm mt-1">Sat: 9:00 AM – 1:00 PM</p>
              </div>
            </div>

            {/* Right — form */}
            <div className="md:col-span-3">
              {success ? (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 text-center">
                  <div className="w-20 h-20 bg-green-50 border-4 border-green-100 rounded-3xl flex items-center justify-center text-4xl mx-auto mb-5">
                    🎉
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-3"
                    style={{ fontFamily: "'Sora', sans-serif" }}>
                    Message Sent!
                  </h2>
                  <p className="text-slate-500 mb-6">
                    Thank you for reaching out. Our team will respond to your message within 24 hours.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setSuccess(false)}
                      className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl text-sm transition-colors"
                    >
                      Send Another
                    </button>
                    <Link to="/"
                      className="px-5 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium rounded-xl text-sm transition-colors">
                      Go Home
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
                  {error && (
                    <div className="mb-5 bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl p-4">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">

                    {/* Category selector */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                        What's your inquiry about? <span className="text-red-400">*</span>
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {CATEGORIES.map((c) => (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => set("category", c.value)}
                            className={`flex flex-col items-start p-3 rounded-xl border text-left transition-all ${
                              form.category === c.value
                                ? "border-orange-400 bg-orange-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <span className="text-base mb-1">{c.label.split(" ")[0]}</span>
                            <p className="text-xs font-semibold text-slate-700 leading-tight">
                              {c.label.split(" ").slice(1).join(" ")}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Name + Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                          Full Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          required
                          value={form.name}
                          onChange={(e) => set("name", e.target.value)}
                          placeholder="Jane Wanjiru"
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                          Email Address <span className="text-red-400">*</span>
                        </label>
                        <input
                          required
                          type="email"
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                          placeholder="jane@example.com"
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Phone + Subject */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                          Phone <span className="text-slate-400 font-normal normal-case">(optional)</span>
                        </label>
                        <input
                          value={form.phone}
                          onChange={(e) => set("phone", e.target.value)}
                          placeholder="07XXXXXXXX"
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                          Subject <span className="text-red-400">*</span>
                        </label>
                        <input
                          required
                          value={form.subject}
                          onChange={(e) => set("subject", e.target.value)}
                          placeholder="e.g. Need help with drivers"
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        Message <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        required
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                        placeholder="Tell us more about what you need..."
                        rows={5}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none"
                      />
                      <p className="text-slate-400 text-xs mt-1 text-right">
                        {form.message.length}/2000
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl shadow-md shadow-orange-200 transition-all flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                          </svg>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}